import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import civiCircleLogo from "@/assets/civicircle-logo.jpg";
import { AuthService, ZulipService, RosterService } from "@/services/APIs";
import CitycollabNavigationRoutes from "@/utils/navigationRoutes";
import { validatePassword } from "@/utils/passwordValidation";
import { setLocalAccessToken, setLocalRefreshToken, setLocalUserInfo } from "@/lib/storeManager";

const authSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }).max(255),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" })
    .max(100),
  name: z
    .string()
    .trim()
    .min(2, { message: "Name must be at least 2 characters" })
    .max(100)
    .optional(),
});

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
  });
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [referCode, setReferCode] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const memberId = localStorage.getItem("memberId");
    const userId = localStorage.getItem("userId");

    if (accessToken) {
      // If we have access token, check if we are fully registered (have a real memberId)
      if (memberId && memberId !== userId) {
        navigate("/home");
      } else {
        // If we are logged in but not registered, we should ideally be on MemberRegistrationPage
        // However, we don't force redirect here yet, let the user see the Sign In if they want,
        // but if they are already logged in, maybe we should just send them to MemberRegistrationPage
        navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
      }
    }

    // Get organization name from localStorage
    const orgName = localStorage.getItem("OrganizationName");
    setOrganizationName(orgName);
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("refer_code");
    setReferCode(code);
    setIsSignUp(!!code);
    setFormData({ email: "", password: "", fullName: "" });
    setPasswordErrors([]);
  }, [location.search]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear password errors when user types in password
    if (e.target.name === "password" && passwordErrors.length > 0) {
      setPasswordErrors([]);
    }
  };

  const fetchZulipTokenOnce = async () => {
    const existingToken = localStorage.getItem("zulipToken");
    if (existingToken) return;

    try {
      const res = await ZulipService.getZulipToken();
      const token = res.basic_token;
      if (token) {
        localStorage.setItem("zulipToken", token);
        localStorage.setItem("zulipUserId", res.zulip_user_id);
      }
    } catch (err) {
      console.error("Zulip Token fetch failed:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const email = formData.email.trim().toLowerCase();
    const password = formData.password;
    const name = formData.fullName.trim();

    // Password validation for Sign Up
    if (isSignUp) {
      const nameParts = name.split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      const validationResult = validatePassword(password, {
        email: email,
        firstName: firstName,
        lastName: lastName,
      });

      if (!validationResult.isValid) {
        setPasswordErrors(validationResult.errors);
        toast({
          title: "Password Validation Failed",
          description: "Please check the password requirements below.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
    }

    try {
      const schema = isSignUp ? authSchema : authSchema.omit({ name: true });
      const validated = schema.parse({ email, password, name: isSignUp ? name : undefined });

      if (isSignUp) {
        // Sign Up flow
        const authRes = await AuthService.endUserSignup({
          email: validated.email,
          password: validated.password,
          full_name: name || "",
        });

        if (authRes) {
          localStorage.setItem("userId", authRes.user.id);
          localStorage.setItem("accessToken", authRes.access);
          localStorage.setItem("refreshToken", authRes.refresh);
          localStorage.setItem("userInfo", JSON.stringify(authRes.user));

          const userAny = authRes.user as any;
          const tenantId = userAny.tenant_id ?? userAny.tenant ?? localStorage.getItem("tenantId") ?? localStorage.getItem("tenant");
          if (tenantId) {
            localStorage.setItem("tenantId", tenantId);
            localStorage.setItem("tenant", tenantId);
          }

          const organizationId = userAny.organization_id ?? userAny.organizationId ?? localStorage.getItem("OrganizationId");
          if (organizationId) {
            localStorage.setItem("OrganizationId", organizationId);
          }

          localStorage.setItem("signupEmail", email);

          try {
            const checkMemberResponse = await RosterService.checkMemberByEmail(validated.email, referCode || undefined);

            // Capture organizationid directly from response root or data, even if success is false
            const responseOrgId = checkMemberResponse?.organizationid ?? checkMemberResponse?.data?.organizationid;
            if (responseOrgId) {
              localStorage.setItem("OrganizationId", responseOrgId);
            }

            if (checkMemberResponse?.success === true) {
              const memberId = checkMemberResponse?.data?.id;
              if (memberId) {
                localStorage.setItem("memberId", memberId);
              }
              await fetchZulipTokenOnce();
              toast({ title: "Success!", description: "Account created successfully." });
              navigate("/home");
              return;
            } else {
              toast({ title: "Success!", description: "Account created successfully." });
              navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
              return;
            }
          } catch (error) {
            console.error("Error checking member by email:", error);
            toast({ title: "Success!", description: "Account created successfully." });
            navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
            return;
          }
        }
      } else {
        // Sign In flow
        const authRes = await AuthService.login({ email: validated.email, password: validated.password });

        if (authRes) {
          localStorage.setItem("userId", authRes.user.id);
          localStorage.setItem("accessToken", authRes.access);
          localStorage.setItem("refreshToken", authRes.refresh);
          localStorage.setItem("userInfo", JSON.stringify(authRes.user));

          const userAny = authRes.user as any;
          const organizationId = userAny.organization_id ?? userAny.organizationId ?? localStorage.getItem("OrganizationId");
          if (organizationId) {
            localStorage.setItem("OrganizationId", organizationId);
          }

          try {
            const checkMemberResponse = await RosterService.checkMemberByEmail(validated.email, referCode || undefined);

            // Capture organizationid directly from response root or data, even if success is false
            const responseOrgId = checkMemberResponse?.organizationid ?? checkMemberResponse?.data?.organizationid;
            if (responseOrgId) {
              localStorage.setItem("OrganizationId", responseOrgId);
            }

            if (checkMemberResponse?.success === true && checkMemberResponse?.data?.id) {
              const memberId = checkMemberResponse.data.id;
              localStorage.setItem("memberId", memberId);
              await fetchZulipTokenOnce();
              toast({ title: "Welcome back!", description: "You've successfully signed in." });
              navigate("/home");
            } else {
              // User not in roster - redirect to registration
              localStorage.setItem("memberId", authRes.user.id);
              await fetchZulipTokenOnce();
              toast({ title: "Welcome back!", description: "Please complete your registration." });
              navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
            }
          } catch (error) {
            console.error("Error checking member by email during login:", error);
            localStorage.setItem("memberId", authRes.user.id);
            await fetchZulipTokenOnce();
            navigate(CitycollabNavigationRoutes.MemberRegistrationPage);
          }
        }
      }
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.issues[0]?.message ?? "Please check your input.",
          variant: "destructive",
        });
      } else {
        const errorMessage = error?.response?.data?.message ?? error?.message ?? "An unexpected error occurred. Please try again.";
        toast({
          title: isSignUp ? "Sign up failed" : "Sign in failed",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </motion.button>

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-8"
          >
            <img
              src={civiCircleLogo}
              alt="CiviCircle"
              className="h-12 w-12 rounded-xl object-cover shadow-md"
            />
            <span className="font-display font-bold text-2xl text-foreground">
              CiviCircle
            </span>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <h1 className="font-display text-3xl font-bold text-foreground mb-2">
              {isSignUp ? "Create your account" : "Welcome back"}
            </h1>
            <p className="text-muted-foreground">
              {isSignUp
                ? (organizationName ? `Join ${organizationName}'s organization today` : "Join thousands of civic professionals making a difference")
                : "Sign in to continue to your dashboard"}
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="pl-10 h-12"
                    required={isSignUp}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="pl-10 h-12"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="pl-10 pr-10 h-12"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Password Validation Errors */}
            {isSignUp && passwordErrors.length > 0 && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
                <h4 className="mb-2 text-sm font-semibold text-destructive">
                  Password Requirements:
                </h4>
                <ul className="list-inside list-disc space-y-1 text-xs text-destructive">
                  {passwordErrors.map(error => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base group"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {isSignUp ? "Creating account..." : "Signing in..."}
                </div>
              ) : (
                <>
                  {isSignUp ? "Create account" : "Sign in"}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-primary via-primary/95 to-accent overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 mesh-gradient opacity-20" />
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-primary-foreground/10 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="font-display text-4xl font-bold text-primary-foreground mb-4">
              Build Stronger Communities
            </h2>
            <p className="text-primary-foreground/80 text-lg max-w-md">
              Connect with city employees, retired professionals, and sponsors to create meaningful civic impact.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-3 gap-6 mt-12"
          >
            {[
              { value: "5K+", label: "Members" },
              { value: "10+", label: "Cities" },
              { value: "100+", label: "Events" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-primary-foreground/10 backdrop-blur-sm rounded-xl p-4 border border-primary-foreground/20"
              >
                <div className="font-display text-2xl font-bold text-primary-foreground">
                  {stat.value}
                </div>
                <div className="text-sm text-primary-foreground/70">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
